// ============================================================================
// Pixelorama JSON Schema Types
// ============================================================================

/**
 * A single cel (layer content) within a frame.
 * Contains rendering metadata for that layer at that frame.
 */
export interface PixeloramaCel {
  metadata: Record<string, unknown>;
  opacity: number;
  ui_color: string;
  z_index: number;
}

/**
 * A single frame in the animation timeline.
 * Duration is a multiplier against the base FPS timing.
 */
export interface PixeloramaFrame {
  cels: PixeloramaCel[];
  duration: number;
  metadata: Record<string, unknown>;
  /** Optional user data string for frame-specific metadata (e.g., "min:0.1; max:20") */
  user_data?: string;
}

/**
 * An animation tag defining a named sequence of frames.
 * Frame indices are 1-based, inclusive.
 */
export interface PixeloramaTag {
  color: string;
  from: number;
  name: string;
  to: number;
}

/**
 * Layer definition in the Pixelorama project.
 */
export interface PixeloramaLayer {
  blend_mode: number;
  clipping_mask: boolean;
  effects: unknown[];
  locked: boolean;
  metadata: Record<string, unknown>;
  name: string;
  new_cels_linked: boolean;
  opacity: number;
  parent: number;
  type: number;
  ui_color: string;
  visible: boolean;
}

/**
 * Root schema for Pixelorama's exported JSON.
 * Only includes fields relevant for spritesheet loading.
 */
export interface PixeloramaJson {
  export_file_name: string;
  fps: number;
  frames: PixeloramaFrame[];
  layers: PixeloramaLayer[];
  size_x: number;
  size_y: number;
  tags: PixeloramaTag[];
}

/**
 * Simple registry for spritesheet dimensions.
 * Populated by SpritesheetMetadataService on the server and
 * SpritesheetService on the client.
 */
export interface SpritesheetMetadata {
  width: number;
  height: number;
}

const registry: Record<string, SpritesheetMetadata> = {};

export const SpritesheetMetadataRegistry = {
  register(id: string, metadata: SpritesheetMetadata): void {
    registry[id] = metadata;
  },

  get(id: string): SpritesheetMetadata | undefined {
    return registry[id];
  },

  /**
   * Helper to get offsets for a centered hitbox of a given size.
   * Assumes x is horizontal center and y is base of sprite.
   */
  getHitboxOffsets(id: string): {
    width: number;
    height: number;
    offsetX: number;
    offsetY: number;
  } {
    const meta = registry[id] || { width: 16, height: 32 };
    return {
      width: meta.width,
      height: meta.height,
      offsetX: -meta.width / 2,
      offsetY: -meta.height,
    };
  },
};
